import { z } from 'zod'
import { centsSchema, isoDateTimeSchema, slugSchema } from './common'

export const scholarshipProgramInputSchema = z.object({
  slug: slugSchema,
  name: z.string().trim().min(1).max(160),
  summary: z.string().max(400).nullable().optional(),
  descriptionMd: z.string().max(20000).nullable().optional(),
  eligibilityMd: z.string().max(10000).nullable().optional(),
  cycleYear: z.number().int().min(2020).max(2100),
  awardAmountCents: centsSchema.default(0),
  awardCount: z.number().int().min(1).max(100).default(1),
  opensAt: isoDateTimeSchema.nullable().optional(),
  closesAt: isoDateTimeSchema.nullable().optional(),
  reviewDeadline: isoDateTimeSchema.nullable().optional(),
  blindReview: z.boolean().default(true),
  status: z.enum(['draft', 'open', 'reviewing', 'awarded', 'closed']).default('draft'),
  programId: z.string().nullable().optional(),
  fundId: z.string().nullable().optional(),
  /** When creating: reuse an existing form or let the service scaffold one. */
  formId: z.string().nullable().optional(),
})
export type ScholarshipProgramInput = z.infer<typeof scholarshipProgramInputSchema>

export const awardInputSchema = z.object({
  applicationId: z.string(),
  amountCents: centsSchema,
  notes: z.string().max(2000).nullable().optional(),
  notify: z.boolean().default(true),
  message: z.string().max(4000).nullable().optional(),
})

export const awardStatusSchema = z.object({
  status: z.enum(['proposed', 'offered', 'accepted', 'declined', 'disbursed']),
  notes: z.string().max(2000).nullable().optional(),
})

export const bulkAssignSchema = z.object({
  reviewerUserIds: z.array(z.string()).min(1),
  dueAt: isoDateTimeSchema.nullable().optional(),
  /** Assign only applications not yet assigned to anyone. */
  onlyUnassigned: z.boolean().default(true),
})

export interface PublicScholarship {
  id: string
  slug: string
  name: string
  summary: string | null
  descriptionHtml: string
  eligibilityHtml: string
  cycleYear: number
  awardAmountCents: number
  awardCount: number
  opensAt: string | null
  closesAt: string | null
  status: string
  isOpen: boolean
  formSlug: string | null
  campaign: {
    id: string
    slug: string
    name: string
    raisedCents: number
    goalCents: number | null
  } | null
  fund: { id: string; name: string; code: string } | null
  /** Where "Donate" goes for this program: `?campaign=` when it has an active campaign, else `?fund=`. */
  donateHref: string
}

/** GET /public/scholarships: every non-draft program plus what links and the home page need. */
export interface PublicScholarshipsPayload {
  items: PublicScholarship[]
  /** Server time the payload was built (responses are cached for 60 s). */
  at: string
  /** Slugs of programs accepting applications at `at`, soonest deadline first. */
  openSlugs: string[]
  /** Where a "Scholarships" link points: the one open program's page, else the list. */
  href: string
  /** The next program scheduled to open (status open, opens after `at`). */
  next: { slug: string; name: string; opensAt: string } | null
  /** The general scholarship fund, for "give to the scholarship fund" links. */
  fund: { code: string; name: string } | null
}

type WindowFields = Pick<PublicScholarship, 'status' | 'opensAt' | 'closesAt'>

/**
 * Accepting applications: status `open` and `at` inside opens_at..closes_at (both inclusive,
 * matching `isBetween`). Pure, so the worker and the browser compute it the same way: the browser
 * re-evaluates against its own clock, so a cached payload never keeps a closed cycle "open".
 */
export function scholarshipIsOpen(s: WindowFields, at: string): boolean {
  if (s.status !== 'open') return false
  const t = Date.parse(at)
  if (s.opensAt && t < Date.parse(s.opensAt)) return false
  if (s.closesAt && t > Date.parse(s.closesAt)) return false
  return true
}

/** Status `open` but the window has not started yet. */
export function scholarshipIsUpcoming(s: WindowFields, at: string): boolean {
  return s.status === 'open' && !!s.opensAt && Date.parse(s.opensAt) > Date.parse(at)
}

const closesKey = (s: WindowFields) =>
  s.closesAt ? Date.parse(s.closesAt) : Number.POSITIVE_INFINITY

/** Programs accepting applications at `at`, soonest deadline first. */
export function openScholarships<T extends WindowFields>(items: T[], at: string): T[] {
  return items.filter((s) => scholarshipIsOpen(s, at)).sort((a, b) => closesKey(a) - closesKey(b))
}

/** The next program to open after `at`, or null. */
export function nextScholarship<T extends WindowFields>(items: T[], at: string): T | null {
  return (
    items
      .filter((s) => scholarshipIsUpcoming(s, at))
      .sort((a, b) => Date.parse(a.opensAt ?? '') - Date.parse(b.opensAt ?? ''))[0] ?? null
  )
}

/** Exactly one open program → its page; otherwise the list. */
export function scholarshipsHref(items: (WindowFields & { slug: string })[], at: string): string {
  const open = openScholarships(items, at)
  return open.length === 1 && open[0] ? `/scholarships/${open[0].slug}` : '/scholarships'
}

/** The next instant after `at` when some program opens or closes (when links and the hero change). */
export function nextScholarshipBoundary(items: WindowFields[], at: string): string | null {
  const t = Date.parse(at)
  let best: number | null = null
  for (const s of items) {
    if (s.status !== 'open') continue
    // closes_at is inclusive, so the switch happens just after it.
    const candidates = [
      s.opensAt ? Date.parse(s.opensAt) : null,
      s.closesAt ? Date.parse(s.closesAt) + 1 : null,
    ]
    for (const ms of candidates)
      if (ms !== null && ms > t && (best === null || ms < best)) best = ms
  }
  return best === null ? null : new Date(best).toISOString()
}

/** Donate link for a program: its active campaign, else its fund, else the general form. */
export function scholarshipDonateHref(s: {
  campaign: { id: string; status: string } | null
  fund: { code: string } | null
}): string {
  if (s.campaign && s.campaign.status === 'active')
    return `/donate?campaign=${encodeURIComponent(s.campaign.id)}`
  if (s.fund) return `/donate?fund=${encodeURIComponent(s.fund.code)}`
  return '/donate'
}
